import { api } from "../../api/client.js";
import type { ActiveRunPhase, ActiveRunSummary, RunDetail, RunLiveSnapshot, TerminalRunHydration, TerminalRunStatus } from "../../api/types.js";

export type StopRunProviderStatus = "cancelled" | "not_found" | "already_finished" | "provider_unavailable";

export type StopRunResponse = {
  ok: boolean;
  stop: { ok: boolean; status: StopRunProviderStatus };
  run?: { id: string; sessionId: string; status: ActiveRunPhase | TerminalRunStatus | string };
};

export async function getActiveRun(): Promise<ActiveRunSummary | null> {
  const data = await api<{ ok: boolean; activeRun: ActiveRunSummary | null }>("/api/runs/active");
  return data.activeRun ?? null;
}

export async function getRun(runId: string): Promise<RunDetail | null> {
  try {
    const data = await api<{ ok: boolean; run: RunDetail }>(`/api/runs/${encodeURIComponent(runId)}`);
    return data.run;
  } catch (error) {
    // The runs API answers an owner-scoped miss with {error:"not found"};
    // callers must distinguish that from a network failure.
    if (error instanceof Error && error.message === "not found") return null;
    throw error;
  }
}

export async function getRunRecovery(runId: string, liveAfterRevision: number): Promise<{
  run: RunDetail;
  liveSnapshot: RunLiveSnapshot | null;
  terminalHydration: TerminalRunHydration | null;
} | null> {
  try {
    return await api<{ ok: boolean; run: RunDetail; liveSnapshot: RunLiveSnapshot | null; terminalHydration: TerminalRunHydration | null }>(
      `/api/runs/${encodeURIComponent(runId)}?liveAfterRevision=${encodeURIComponent(String(liveAfterRevision))}`
    );
  } catch (error) {
    if (error instanceof Error && error.message === "not found") return null;
    throw error;
  }
}

export async function stopRun(input: { runId: string; sessionId: string }): Promise<StopRunResponse> {
  return api<StopRunResponse>("/api/chat/stop", { method: "POST", body: JSON.stringify(input) });
}
